import { t } from "i18next";
import { z } from "zod";

import {
  APPLICANT_SSN_TYPES,
  type ApplicantSsnType,
  getDefaultIncomeType,
  MAX_PHONES_PER_PERSON,
  PHONE_EXTENSION_MAX_LENGTH,
  PHONE_NUMBER_DIGITS,
} from "./constants";

// Shared message for every required field. The field name is already visible
// above each inline error, so a single generic message keeps them consistent.
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
const REQUIRED_MESSAGE = "This is a required field.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
const PHONE_REQUIRED_MESSAGE = "Please enter the area code and 7 digit Phone Number.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
const DUPLICATE_PHONE_MESSAGE = "Cannot have duplicate Phone Numbers. Please enter a unique Phone Number.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
const EMAIL_REQUIRED_MESSAGE = "Please enter Email Address or Select Email Not Available option.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
const EMAIL_INVALID_MESSAGE = "Please enter a valid email address.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
const EXTENSION_INVALID_MESSAGE = "Extension must be up to 4 digits.";
const SSN_DUPLICATE_MESSAGE =
  // eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy; i18n extraction tracked as a follow-up (OR-22059)
  "Driver and 2nd Signer cannot have same Social Security Number. Please enter unique SSN for Driver and 2nd Signer.";

const EMAIL_REGEX = /^[\w%+.-]+@[\d.A-Za-z-]+\.[A-Za-z]{2,6}$/;

// Number inputs write "" (not undefined) while empty so RHF doesn't unset the
// field and fall back to defaultValues (see FieldRenderer). Coerce that back
// to undefined here so z.number()/.optional()/.gt() see the value they expect.
const emptyStringToUndefined = (value: unknown) => (value === "" ? undefined : value);

// ---------------------------------------------------------------------------
// Income schema
// ---------------------------------------------------------------------------
export const incomeSchema = z.object({
  id: z.string(),
  // EMPLOYMENT_TYPES codes: 6=Employee, 7=Self-Employed, 8=Fixed Income,
  // 9=Temp Service, 10=Unemployed, 11=Cash Income.
  incomeType: z.enum(["6", "7", "8", "9", "10", "11"], { error: REQUIRED_MESSAGE }),
  employerName: z.string().min(1, REQUIRED_MESSAGE),
  occupation: z.string().optional(),
  streetNumber: z.string().optional(),
  streetName: z.string().optional(),
  suite: z.string().optional(),
  zip: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  verificationPhone: z.string().min(1, REQUIRED_MESSAGE),
  verificationPhoneExt: z.string().optional(),
  differentWorkLocation: z.boolean(),
  grossMonthlyPay: z.preprocess(emptyStringToUndefined, z.number({ error: REQUIRED_MESSAGE }).gt(0, REQUIRED_MESSAGE)),
  hireDate: z.string().min(1, REQUIRED_MESSAGE),
  payPerPeriod: z.preprocess(emptyStringToUndefined, z.number().optional()),
  payFrequency: z.enum(["Weekly", "Bi-Weekly", "2x-Monthly", "Monthly", "Annual"]).optional(),
  grossNet: z.enum(["Gross", "Net"]).optional(),
  additionalInformation: z.string().optional(),
  // ---------------------------------------------------------------------------
  // Income Calculator fields (OR-21766) — additive optional; not required for
  // block validation so the calculator can be unused without errors.
  // ---------------------------------------------------------------------------
  payDate: z.string().optional(),
  payPeriodEndDate: z.string().optional(),
  grossPayYtd: z.preprocess(emptyStringToUndefined, z.number().optional()),
  // OR-22309 P3: paid-on-1st-and-15th toggle (2x-Monthly pay frequency).
  isPaidOn1stAnd15: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Bank account schema (additive — does not touch incomeSchema)
// ---------------------------------------------------------------------------
// Ported from dealer-ui bankAccountSchema; `id` and nullable transforms
// intentionally dropped (local-only foundation, workbench uses undefined for
// empty optionals — matches the income schema convention above).
export const bankAccountSchema = z
  .object({
    isSavingsAccount: z.boolean().optional(),
    isCheckingAccount: z.boolean().optional(),
    isPrepaidDebit: z.boolean().optional(),
    bankName: z.string().max(100, t("review.bank.bankNameTooLong")).optional(),
  })
  .superRefine((data, context) => {
    // Bank Name only renders once an account type is checked; require it while
    // it is visible so a hidden field can never block Save.
    const anyAccountTypeSelected = Boolean(data.isSavingsAccount || data.isCheckingAccount || data.isPrepaidDebit);
    if (anyAccountTypeSelected && (data.bankName ?? "").trim() === "") {
      context.addIssue({ code: "custom", path: ["bankName"], message: REQUIRED_MESSAGE });
    }
  });

// ---------------------------------------------------------------------------
// Phone schema (OR-21996)
// ---------------------------------------------------------------------------
// `id` is a stable crypto.randomUUID() key — never the array index.
// Fields are lenient strings; masking and format validation are deferred.
export const phoneSchema = z.object({
  id: z.string(),
  number: z.string().default(""),
  type: z.enum(["Personal", "Work"]).default("Personal"),
  extension: z.string().default(""),
});

// ---------------------------------------------------------------------------
// Customer Information schema (OR-21996)
// ---------------------------------------------------------------------------
export const customerInfoSchema = z
  .object({
    // A. Personal Details
    ssnOrItin: z.string().default(""),
    dateOfBirth: z.string().default(""),
    driverLicense: z.string().default(""),
    holdCouponBook: z.enum(["No", "Yes"]).default("No"),
    firstName: z.string().default(""),
    middleInitial: z.string().default(""),
    lastName: z.string().default(""),
    suffix: z.string().default(""),

    // B. Residence Information — Physical
    physicalStreetNumber: z.string().default(""),
    physicalStreetName: z.string().default(""),
    physicalApt: z.string().default(""),
    physicalZip: z.string().default(""),
    physicalCity: z.string().default(""),
    physicalState: z.string().default(""),

    // B. Residence Information — Mailing
    mailingSameAsCurrent: z.boolean().default(false),
    mailingStreetNumber: z.string().default(""),
    mailingStreetName: z.string().default(""),
    mailingApt: z.string().default(""),
    mailingZip: z.string().default(""),
    mailingCity: z.string().default(""),
    mailingState: z.string().default(""),

    // C. Contact Information
    phones: z.array(phoneSchema).min(1).max(MAX_PHONES_PER_PERSON),
    email: z.string().default(""),
    emailNotAvailable: z.boolean().default(false),
  })
  .superRefine((data, context) => {
    // --- Phone validation ---
    const seen = new Set<string>();
    for (const [index, phone] of data.phones.entries()) {
      const digits = phone.number.replaceAll(/\D/g, "");

      // Required / length check
      if (index === 0) {
        // Primary: always require exactly 10 digits
        if (digits.length !== PHONE_NUMBER_DIGITS) {
          context.addIssue({ code: "custom", path: ["phones", index, "number"], message: PHONE_REQUIRED_MESSAGE });
        }
      } else if (digits.length > 0 && digits.length !== PHONE_NUMBER_DIGITS) {
        // Secondary: only validate when something has been entered
        context.addIssue({ code: "custom", path: ["phones", index, "number"], message: PHONE_REQUIRED_MESSAGE });
      }

      // Extension validation — only applies to Work-type phones (the extension
      // input is hidden and cleared when switching away from Work, so a non-Work
      // row with a leftover extension value should never surface an error).
      if (
        phone.type === "Work" &&
        phone.extension !== "" &&
        (!/^\d*$/.test(phone.extension) || phone.extension.length > PHONE_EXTENSION_MAX_LENGTH)
      ) {
        context.addIssue({
          code: "custom",
          path: ["phones", index, "extension"],
          message: EXTENSION_INVALID_MESSAGE,
        });
      }

      // Duplicate check (only for complete 10-digit numbers)
      if (digits.length === PHONE_NUMBER_DIGITS) {
        if (seen.has(digits)) {
          context.addIssue({ code: "custom", path: ["phones", index, "number"], message: DUPLICATE_PHONE_MESSAGE });
        } else {
          seen.add(digits);
        }
      }
    }

    // --- Email validation ---
    if (!data.emailNotAvailable) {
      const trimmed = data.email.trim();
      if (trimmed === "") {
        context.addIssue({ code: "custom", path: ["email"], message: EMAIL_REQUIRED_MESSAGE });
      } else if (!EMAIL_REGEX.test(trimmed)) {
        context.addIssue({ code: "custom", path: ["email"], message: EMAIL_INVALID_MESSAGE });
      }
    }
  });

// ---------------------------------------------------------------------------
// Person schema
// ---------------------------------------------------------------------------
export const personSchema = z.object({
  incomes: z.array(incomeSchema).min(1).max(3),
  bankAccount: bankAccountSchema,
  customerInfo: customerInfoSchema,
});

// ---------------------------------------------------------------------------
// Root signers schema
// ---------------------------------------------------------------------------
export const signersSchema = z
  .object({
    driver: personSchema,
    secondSigner: personSchema.optional(),
  })
  .superRefine((data, context) => {
    // Driver and 2nd Signer cannot share the same SSN/ITIN. Compare canonical
    // digits and only flag complete (9-digit) matches so partial entry is quiet.
    const driverSsn = data.driver.customerInfo.ssnOrItin.replaceAll(/\D/g, "");
    const secondSsn = (data.secondSigner?.customerInfo.ssnOrItin ?? "").replaceAll(/\D/g, "");
    if (driverSsn.length === 9 && driverSsn === secondSsn) {
      context.addIssue({
        code: "custom",
        path: ["driver", "customerInfo", "ssnOrItin"],
        message: SSN_DUPLICATE_MESSAGE,
      });
      context.addIssue({
        code: "custom",
        path: ["secondSigner", "customerInfo", "ssnOrItin"],
        message: SSN_DUPLICATE_MESSAGE,
      });
    }
  });

// ---------------------------------------------------------------------------
// Inferred TypeScript types
// ---------------------------------------------------------------------------
export type IncomeInput = z.infer<typeof incomeSchema>;
export type BankAccountInput = z.infer<typeof bankAccountSchema>;
export type PhoneInput = z.infer<typeof phoneSchema>;
export type CustomerInfoInput = z.infer<typeof customerInfoSchema>;
export type PersonInput = z.infer<typeof personSchema>;
export type SignersInput = z.infer<typeof signersSchema>;

// ---------------------------------------------------------------------------
// Factories — always use crypto.randomUUID() for stable ids (never length+1)
// ---------------------------------------------------------------------------
/**
 * Creates a blank bank-account object with all account-type flags set to false
 * and bankName empty. All checkboxes start unchecked so Bank Name is hidden.
 * @returns {BankAccountInput} A new BankAccountInput with default values.
 */
export function makeEmptyBankAccount(): BankAccountInput {
  return {
    isSavingsAccount: false,
    isCheckingAccount: false,
    isPrepaidDebit: false,
    bankName: "",
  };
}

/**
 * Creates a blank phone entry with a stable unique id.
 * @returns {PhoneInput} A new PhoneInput with default values.
 */
export function makeEmptyPhone(): PhoneInput {
  return { id: crypto.randomUUID(), number: "", type: "Personal", extension: "" };
}

/**
 * Creates a blank customer info object with all fields at their defaults and
 * one seeded primary phone.
 * @returns {CustomerInfoInput} A new CustomerInfoInput with default values.
 */
export function makeEmptyCustomerInfo(): CustomerInfoInput {
  return {
    ssnOrItin: "",
    dateOfBirth: "",
    driverLicense: "",
    holdCouponBook: "No",
    firstName: "",
    middleInitial: "",
    lastName: "",
    suffix: "",
    physicalStreetNumber: "",
    physicalStreetName: "",
    physicalApt: "",
    physicalZip: "",
    physicalCity: "",
    physicalState: "",
    mailingSameAsCurrent: false,
    mailingStreetNumber: "",
    mailingStreetName: "",
    mailingApt: "",
    mailingZip: "",
    mailingCity: "",
    mailingState: "",
    phones: [makeEmptyPhone()],
    email: "",
    emailNotAvailable: false,
  };
}

/**
 * Creates a blank income entry with a stable unique id. The default income type
 * honors the applicant's SSN/ITIN status — Employee ("6") for an SSN applicant,
 * the first allowed option (Self-Employed "7") for a non-SSN one — so a seeded
 * block never starts on an option the applicant may not select.
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status (defaults to SSN).
 * @returns {IncomeInput} A new IncomeInput with all required fields set to empty/default values.
 */
export function makeEmptyIncome(applicantSsnType: ApplicantSsnType = APPLICANT_SSN_TYPES.SSN): IncomeInput {
  return {
    id: crypto.randomUUID(),
    incomeType: getDefaultIncomeType(applicantSsnType),
    employerName: "",
    occupation: "",
    streetNumber: "",
    streetName: "",
    suite: "",
    zip: "",
    city: "",
    state: "",
    verificationPhone: "",
    verificationPhoneExt: "",
    differentWorkLocation: false,
    grossMonthlyPay: 0,
    hireDate: "",
    payPerPeriod: undefined,
    payFrequency: "Monthly",
    grossNet: undefined,
    additionalInformation: "",
    payDate: undefined,
    payPeriodEndDate: undefined,
    grossPayYtd: undefined,
    isPaidOn1stAnd15: false,
  };
}
